import { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useDebouncedSearch } from './useDebouncedSearch';
import { PaginatedBody, SearchQuery, SortOrderQuery } from '@/api/types';

type UseInfiniteListProps<T, F> = {
  query: (
    filters: SearchQuery & SortOrderQuery & Partial<F>,
    enabled?: boolean,
  ) => UseInfiniteQueryResult<InfiniteData<PaginatedBody<T>, unknown>, Error>;
  queryParams: F & SortOrderQuery;
  searchTerm: string;
  enabled?: boolean;
};

export const useInfiniteList = <T extends Record<string, unknown>, F extends Record<string, unknown>>({
  query,
  queryParams,
  searchTerm,
  enabled = true,
}: UseInfiniteListProps<T, F>) => {
  const searchQuery = useDebouncedSearch(searchTerm);
  const queryResult = query({ ...queryParams, searchQuery }, enabled);
  const { data } = queryResult;

  const list = useMemo<T[]>(() => {
    if (!data || !data?.pages) return [];
    return data.pages.map((page) => page.items).flat();
  }, [data]);

  return { ...queryResult, list };
};
